/**
 * TypographyV2 - Design System Typography Components
 *
 * This file provides typography components using the Catalyst Design System tokens.
 * Components follow a Tailwind-style naming convention for easy selection by LLMs.
 *
 * ## QUICK REFERENCE
 *
 * SIZES (pick one):
 * - xs:   10px / 12px line-height  (extra small labels, captions)
 * - sm:   12px / 16px line-height  (small labels, helper text)
 * - md:   14px / 20px line-height  (body text, default UI text)
 * - base: 16px / 24px line-height  (prominent body text)
 * - xl:   20px / 28px line-height  (subheadings)
 * - 2xl:  24px / 32px line-height  (section headings)
 * - 3xl:  32px / 40px line-height  (page headings)
 * - 4xl:  40px / 48px line-height  (large headings)
 * - 5xl:  48px / 56px line-height  (hero headings)
 * - 6xl:  56px / 64px line-height  (display headings)
 *
 * WEIGHTS (pick one):
 * - Light:     300 (subtle, decorative text)
 * - Regular:   400 (body text, default)
 * - Medium:    500 (slightly emphasized)
 * - Semibold:  600 (labels, buttons)
 * - Bold:      700 (headings, strong emphasis)
 * - Extrabold: 800 (maximum emphasis)
 *
 * ## USAGE PATTERNS
 *
 * ### Pattern 1: Pre-styled components (recommended for LLMs)
 * ```tsx
 * import { TextSmRegular, Text2xlBold, TextMdSemibold } from './TypographyV2';
 *
 * <TextSmRegular>Helper text</TextSmRegular>
 * <Text2xlBold>Page Title</Text2xlBold>
 * <TextMdSemibold color="error">Error message</TextMdSemibold>
 * ```
 *
 * ### Pattern 2: Flexible Text component
 * ```tsx
 * import { Text } from './TypographyV2';
 *
 * <Text size="md" weight="semibold" color="primary">Text</Text>
 * <Text size="3xl" weight="bold" as="h1">Heading</Text>
 * ```
 *
 * ## COMMON USE CASES
 *
 * - Page title:       Text3xlBold or Text4xlBold
 * - Section heading:  Text2xlSemibold or TextXlBold
 * - Subheading:       TextXlMedium or TextBaseSemibold
 * - Body text:        TextMdRegular or TextBaseRegular
 * - Small/helper:     TextSmRegular
 * - Labels:           TextSmMedium or TextXsMedium
 * - Error text:       TextSmRegular with color="error"
 * - Success text:     TextSmRegular with color="success"
 */

import styled, { css } from "styled-components/macro";

// =============================================================================
// TYPES
// =============================================================================

export type TextSize =
  | "xs"
  | "sm"
  | "md"
  | "base"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl";

export type TextWeight =
  | "light"
  | "regular"
  | "medium"
  | "semibold"
  | "bold"
  | "extrabold";

export type TextColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "error"
  | "success"
  | "warning"
  | "caution"
  | "info"
  | "brand"
  | "inverse";

export type TextElement =
  | "span"
  | "p"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "label"
  | "strong"
  | "em";

export interface TextProps {
  /** Font size - xs (10px) to 6xl (56px) */
  size?: TextSize;
  /** Font weight - light (300) to extrabold (800) */
  weight?: TextWeight;
  /** Text color - uses semantic color tokens */
  color?: TextColor;
  /** HTML element to render */
  as?: TextElement;
  /** Additional className */
  className?: string;
  /** Child content */
  children?: React.ReactNode;
}

// =============================================================================
// TOKEN MAPPING
// =============================================================================

const sizeToFontToken: Record<TextSize, string> = {
  xs: "font_size_xs",
  sm: "font_size_sm",
  md: "font_size_md",
  base: "font_size_base",
  xl: "font_size_xl",
  "2xl": "font_size_2xl",
  "3xl": "font_size_3xl",
  "4xl": "font_size_4xl",
  "5xl": "font_size_5xl",
  "6xl": "font_size_6xl",
};

const sizeToLineHeightToken: Record<TextSize, string> = {
  xs: "line_height_xs",
  sm: "line_height_sm",
  md: "line_height_md",
  base: "line_height_base",
  xl: "line_height_xl",
  "2xl": "line_height_2xl",
  "3xl": "line_height_3xl",
  "4xl": "line_height_4xl",
  "5xl": "line_height_5xl",
  "6xl": "line_height_6xl",
};

const weightToToken: Record<TextWeight, string> = {
  light: "font_weight_light",
  regular: "font_weight_regular",
  medium: "font_weight_medium",
  semibold: "font_weight_semibold",
  bold: "font_weight_bold",
  extrabold: "font_weight_extrabold",
};

const colorToToken: Record<TextColor, string> = {
  primary: "color_text_primary",
  secondary: "color_text_secondary",
  tertiary: "color_text_tertiary",
  error: "color_text_error",
  success: "color_text_success",
  warning: "color_text_warning",
  caution: "color_text_caution",
  info: "color_text_info",
  brand: "color_text_brand",
  inverse: "color_text_inverse",
};

// =============================================================================
// BASE STYLES
// =============================================================================

const getTextStyles = (
  size: TextSize,
  weight: TextWeight,
  color: TextColor = "primary"
) => css`
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-size: ${({ theme }) =>
    theme.resolve(
      sizeToFontToken[size] as keyof typeof theme.allTokensForTheme
    )};
  line-height: ${({ theme }) =>
    theme.resolve(
      sizeToLineHeightToken[size] as keyof typeof theme.allTokensForTheme
    )};
  font-weight: ${({ theme }) =>
    theme.resolve(
      weightToToken[weight] as keyof typeof theme.allTokensForTheme
    )};
  color: ${({ theme }) =>
    theme.resolve(colorToToken[color] as keyof typeof theme.allTokensForTheme)};
  margin: 0;
`;

// =============================================================================
// FLEXIBLE TEXT COMPONENT
// =============================================================================

/**
 * Flexible text component with size, weight, and color props.
 *
 * @example
 * ```tsx
 * <Text size="md" weight="semibold">Label text</Text>
 * <Text size="3xl" weight="bold" as="h1">Page Title</Text>
 * <Text size="sm" weight="regular" color="error">Error message</Text>
 * ```
 */
export const Text = styled.span.attrs<TextProps>((props) => ({
  as: props.as || "span",
}))<TextProps>`
  ${({ size = "md", weight = "regular", color = "primary" }) =>
    getTextStyles(size, weight, color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - Extra Small (10px)
// =============================================================================

/** 10px light (300) - Extra small decorative text */
export const TextXsLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "light", color)}
`;

/** 10px regular (400) - Extra small labels, timestamps */
export const TextXsRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "regular", color)}
`;

/** 10px medium (500) - Extra small emphasized labels */
export const TextXsMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "medium", color)}
`;

/** 10px semibold (600) - Extra small strong labels */
export const TextXsSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "semibold", color)}
`;

/** 10px bold (700) - Extra small bold text */
export const TextXsBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "bold", color)}
`;

/** 10px extrabold (800) - Extra small maximum emphasis */
export const TextXsExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xs", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - Small (12px)
// =============================================================================

/** 12px light (300) - Small decorative text */
export const TextSmLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "light", color)}
`;

/** 12px regular (400) - Helper text, captions, small labels */
export const TextSmRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "regular", color)}
`;

/** 12px medium (500) - Small emphasized labels */
export const TextSmMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "medium", color)}
`;

/** 12px semibold (600) - Small strong labels, badges */
export const TextSmSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "semibold", color)}
`;

/** 12px bold (700) - Small bold text */
export const TextSmBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "bold", color)}
`;

/** 12px extrabold (800) - Small maximum emphasis */
export const TextSmExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("sm", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - Medium (14px)
// =============================================================================

/** 14px light (300) - Medium decorative text */
export const TextMdLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "light", color)}
`;

/** 14px regular (400) - Default body text, UI text */
export const TextMdRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "regular", color)}
`;

/** 14px medium (500) - Slightly emphasized body text */
export const TextMdMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "medium", color)}
`;

/** 14px semibold (600) - Labels, button text */
export const TextMdSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "semibold", color)}
`;

/** 14px bold (700) - Bold body text */
export const TextMdBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "bold", color)}
`;

/** 14px extrabold (800) - Medium maximum emphasis */
export const TextMdExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("md", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - Base (16px)
// =============================================================================

/** 16px light (300) - Base decorative text */
export const TextBaseLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "light", color)}
`;

/** 16px regular (400) - Prominent body text */
export const TextBaseRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "regular", color)}
`;

/** 16px medium (500) - Emphasized base text */
export const TextBaseMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "medium", color)}
`;

/** 16px semibold (600) - Strong base text, small headings */
export const TextBaseSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "semibold", color)}
`;

/** 16px bold (700) - Bold base text */
export const TextBaseBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "bold", color)}
`;

/** 16px extrabold (800) - Base maximum emphasis */
export const TextBaseExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("base", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - XL (20px)
// =============================================================================

/** 20px light (300) - XL decorative text */
export const TextXlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "light", color)}
`;

/** 20px regular (400) - Subheadings, lead text */
export const TextXlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "regular", color)}
`;

/** 20px medium (500) - Emphasized subheadings */
export const TextXlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "medium", color)}
`;

/** 20px semibold (600) - Strong subheadings */
export const TextXlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "semibold", color)}
`;

/** 20px bold (700) - Bold subheadings */
export const TextXlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "bold", color)}
`;

/** 20px extrabold (800) - XL maximum emphasis */
export const TextXlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("xl", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - 2XL (24px)
// =============================================================================

/** 24px light (300) - 2XL decorative text */
export const Text2xlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "light", color)}
`;

/** 24px regular (400) - Section headings */
export const Text2xlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "regular", color)}
`;

/** 24px medium (500) - Emphasized section headings */
export const Text2xlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "medium", color)}
`;

/** 24px semibold (600) - Strong section headings */
export const Text2xlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "semibold", color)}
`;

/** 24px bold (700) - Bold section headings */
export const Text2xlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "bold", color)}
`;

/** 24px extrabold (800) - 2XL maximum emphasis */
export const Text2xlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("2xl", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - 3XL (32px)
// =============================================================================

/** 32px light (300) - 3XL decorative text */
export const Text3xlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "light", color)}
`;

/** 32px regular (400) - Page headings */
export const Text3xlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "regular", color)}
`;

/** 32px medium (500) - Emphasized page headings */
export const Text3xlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "medium", color)}
`;

/** 32px semibold (600) - Strong page headings */
export const Text3xlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "semibold", color)}
`;

/** 32px bold (700) - Bold page headings */
export const Text3xlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "bold", color)}
`;

/** 32px extrabold (800) - 3XL maximum emphasis */
export const Text3xlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("3xl", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - 4XL (40px)
// =============================================================================

/** 40px light (300) - 4XL decorative text */
export const Text4xlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "light", color)}
`;

/** 40px regular (400) - Large headings */
export const Text4xlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "regular", color)}
`;

/** 40px medium (500) - Emphasized large headings */
export const Text4xlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "medium", color)}
`;

/** 40px semibold (600) - Strong large headings */
export const Text4xlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "semibold", color)}
`;

/** 40px bold (700) - Bold large headings */
export const Text4xlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "bold", color)}
`;

/** 40px extrabold (800) - 4XL maximum emphasis */
export const Text4xlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("4xl", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - 5XL (48px)
// =============================================================================

/** 48px light (300) - 5XL decorative text */
export const Text5xlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "light", color)}
`;

/** 48px regular (400) - Hero headings */
export const Text5xlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "regular", color)}
`;

/** 48px medium (500) - Emphasized hero headings */
export const Text5xlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "medium", color)}
`;

/** 48px semibold (600) - Strong hero headings */
export const Text5xlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "semibold", color)}
`;

/** 48px bold (700) - Bold hero headings */
export const Text5xlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "bold", color)}
`;

/** 48px extrabold (800) - 5XL maximum emphasis */
export const Text5xlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("5xl", "extrabold", color)}
`;

// =============================================================================
// PRE-STYLED COMPONENTS - 6XL (56px)
// =============================================================================

/** 56px light (300) - 6XL decorative display text */
export const Text6xlLight = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "light", color)}
`;

/** 56px regular (400) - Display headings */
export const Text6xlRegular = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "regular", color)}
`;

/** 56px medium (500) - Emphasized display headings */
export const Text6xlMedium = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "medium", color)}
`;

/** 56px semibold (600) - Strong display headings */
export const Text6xlSemibold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "semibold", color)}
`;

/** 56px bold (700) - Bold display headings */
export const Text6xlBold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "bold", color)}
`;

/** 56px extrabold (800) - 6XL maximum emphasis */
export const Text6xlExtrabold = styled.span<{ color?: TextColor }>`
  ${({ color = "primary" }) => getTextStyles("6xl", "extrabold", color)}
`;
